import { gsap } from 'gsap';

export default class BlockProjectDetailedSlider {
    static init() {
        document.querySelectorAll('.block-project_detailed-slider').forEach(block => new ProjectSlider(block));
    }
}

class ProjectSlider {
    constructor(block) {
        this.dom = {
            block: block,
            wrapper: block.querySelector('.projects-slider'),
            container: block.querySelector('.slider-container')
        };

        this.config = {
            speed: parseFloat(this.dom.wrapper.dataset.sliderSpeed) || 1.2,
            hoverSlowdown: parseFloat(this.dom.wrapper.dataset.hoverSlowdown) || 0.3,
            dragThreshold: 8,
            rotationIntensity: 1.5,
            maxRotation: 12,
            lerpFactor: 0.04,
            friction: 0.95,
            wheelIntensity: 0.12,
            maxWheelVelocity: 35
        };

        this.state = {
            position: 0,
            targetVelocity: 0,
            smoothedVelocity: 0,
            isDragging: false,
            isHovered: false,
            preventClick: false,
            dragStartX: 0,
            dragStartPos: 0
        };

        this.init();
    }

    isMobile() {
        return window.innerWidth < 576;
    }

    init() {
        if (this.isMobile()) return;

        this.setupSlides();
        // Position initiale : on se place au début du 2ème set (le clone du milieu)
        this.state.position = -this.totalWidth;
        this.setupEvents();
        this.setupQuickSetters();
        this.startLoop();
    }

    setupSlides() {
        const originalSlides = [...this.dom.container.querySelectorAll('.swiper-slide')];
        this.originalSlideCount = originalSlides.length;

        // 1. CLONAGE (Triple le contenu : [Clone] [Original] [Clone])
        originalSlides.forEach(s => this.dom.container.appendChild(s.cloneNode(true)));
        originalSlides.forEach(s => this.dom.container.appendChild(s.cloneNode(true)));

        // 2. CALCUL PRÉCIS (Correction du saut visuel)
        this.calculateDimensions();

        // 3. PRÉPARATION DES DONNÉES
        this.slidesData = [...this.dom.container.querySelectorAll('.swiper-slide')].map(slide => {
            const card = slide.querySelector('.project-card');

            // Sécurité anti-drag natif
            slide.querySelectorAll('a, img').forEach(el => {
                el.setAttribute('draggable', 'false');
                el.ondragstart = () => false;
            });

            return {
                el: slide,
                card: card,
                weight: 0.6 + Math.random() * 0.8,
                setRotation: gsap.quickSetter(card, "rotation", "deg")
            };
        });
    }

    calculateDimensions() {
        // On mesure le premier slide. Comme ils sont en VW, ils sont tous identiques.
        // Utilisation de getBoundingClientRect() pour avoir les décimales (ex: 400.5px)
        // offsetWidth arrondit à l'entier, ce qui cause le décalage cumulé.
        const firstSlide = this.dom.container.querySelector('.swiper-slide');
        if (!firstSlide) return;

        const rect = firstSlide.getBoundingClientRect();
        const style = window.getComputedStyle(firstSlide);
        const marginRight = parseFloat(style.marginRight) || 0;

        // Largeur précise d'un slide + sa marge
        const singleSlideWidth = rect.width + marginRight;

        // Largeur totale d'un SET complet (ex: les 10 slides originaux)
        this.totalWidth = singleSlideWidth * this.originalSlideCount;
    }

    setupQuickSetters() {
        this.setX = gsap.quickSetter(this.dom.container, "x", "px");
    }

    setupEvents() {
        this.dom.wrapper.addEventListener('mouseenter', () => this.state.isHovered = true);
        this.dom.wrapper.addEventListener('mouseleave', () => this.state.isHovered = false);

        // GESTION DU RESIZE (Important pour les layout en VW)
        window.addEventListener('resize', () => {
            const oldTotalWidth = this.totalWidth;
            this.calculateDimensions();

            // On ajuste la position proportionnellement pour ne pas sauter visuellement lors du resize
            if (oldTotalWidth > 0) {
                const ratio = this.totalWidth / oldTotalWidth;
                this.state.position *= ratio;
                this.state.dragStartPos *= ratio;
            }
        });

        // DRAG EVENTS
        const onStart = (e) => {
            if (e.type === 'mousedown' && e.button !== 0) return; // Clic gauche uniquement

            this.state.isDragging = true;
            this.state.preventClick = false;
            this.state.dragStartX = e.clientX || e.touches?.[0].clientX;
            this.state.dragStartPos = this.state.position;
            this.dom.wrapper.classList.add('is-dragging');
        };

        const onMove = (e) => {
            if (!this.state.isDragging) return;

            const x = e.clientX || e.touches?.[0].clientX;
            if (x === undefined) return;

            const delta = x - this.state.dragStartX;

            if (Math.abs(delta) > this.config.dragThreshold) {
                this.state.preventClick = true;
                this.dom.wrapper.classList.add('prevent-click');
            }

            this.state.position = this.state.dragStartPos + delta;
        };

        const onEnd = () => {
            this.state.isDragging = false;
            this.dom.wrapper.classList.remove('is-dragging');
            setTimeout(() => this.dom.wrapper.classList.remove('prevent-click'), 20);
        };

        const onWheel = (e) => {
            if (this.state.isDragging) return;

            // Intercepte uniquement le geste horizontal du trackpad.
            // Le scroll vertical doit rester géré par la page.
            if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

            const delta = e.deltaX;
            if (!delta) return;

            e.preventDefault();
            this.state.targetVelocity -= delta * this.config.wheelIntensity;
            this.state.targetVelocity = Math.max(
                -this.config.maxWheelVelocity,
                Math.min(this.config.maxWheelVelocity, this.state.targetVelocity)
            );
        };

        // Listeners sur le WRAPPER (zone globale)
        this.dom.wrapper.addEventListener('mousedown', onStart);
        this.dom.wrapper.addEventListener('touchstart', onStart, { passive: true });
        this.dom.wrapper.addEventListener('wheel', onWheel, { passive: false });

        window.addEventListener('mousemove', onMove);
        window.addEventListener('touchmove', onMove, { passive: false });

        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchend', onEnd);

        // Clics liens
        this.dom.container.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', (e) => {
                if (this.state.preventClick) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            });
        });
    }

    lerp(start, end, factor) {
        return start + (end - start) * factor;
    }

    startLoop() {
        let lastPos = this.state.position;

        const update = () => {
            if (!this.state.isDragging) {
                const speed = this.state.isHovered ? this.config.speed * this.config.hoverSlowdown : this.config.speed;
                this.state.position -= speed;

                // Inertie
                this.state.position += this.state.targetVelocity;
                this.state.targetVelocity *= this.config.friction;

                if (Math.abs(this.state.targetVelocity) < 0.01) this.state.targetVelocity = 0;
            } else {
                this.state.targetVelocity = this.state.position - lastPos;
            }

            // CORRECTION DES SAUTS ET DE LA ROTATION
            // On vérifie les bornes. Si on dépasse, on téléporte.
            // On doit ajuster 'lastPos' également pour que la vélocité (position - lastPos) ne fasse pas un bond géant.

            if (this.state.position <= -this.totalWidth * 2) {
                const offset = this.totalWidth;
                this.state.position += offset;
                if (this.state.isDragging) this.state.dragStartPos += offset;
                lastPos += offset; // <--- Crucial pour éviter l'inversion de rotation
            }
            else if (this.state.position > -this.totalWidth) {
                const offset = this.totalWidth;
                this.state.position -= offset;
                if (this.state.isDragging) this.state.dragStartPos -= offset;
                lastPos -= offset; // <--- Crucial
            }

            // Calcul de la vélocité après correction des positions
            // (En théorie targetVelocity est calculé avant, mais pour le lissage c'est mieux d'être cohérent)

            const currentVelocity = this.state.position - lastPos;
            // Note: on utilise targetVelocity calculé plus haut pour la physique,
            // mais pour la rotation visuelle lissée, on s'assure qu'il n'y a pas de pic.

            lastPos = this.state.position;

            this.state.smoothedVelocity = this.lerp(
                this.state.smoothedVelocity,
                this.state.targetVelocity,
                this.config.lerpFactor
            );

            this.setX(this.state.position);

            const baseRot = this.state.smoothedVelocity * this.config.rotationIntensity;
            this.slidesData.forEach(slide => {
                const rot = Math.max(Math.min(baseRot * slide.weight, this.config.maxRotation), -this.config.maxRotation);
                slide.setRotation(rot);
            });

            requestAnimationFrame(update);
        };
        update();
    }
}
